import { PROMO_CODE_MAX_LENGTH, PROMO_CODE_PATTERN } from './constants.js';
import { formatCents } from './money.js';

/**
 * Creator codes: pure helpers shared by the server (generation, matching) and
 * the client (validation, display).
 */

/**
 * The canonical form of a code. Shoppers type codes however they like —
 * "rae10", " Rae10 " — and every platform we read from treats them
 * case-insensitively, so matching happens on this form only.
 */
export function normalizePromoCode(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim().toUpperCase();
}

export function isValidPromoCode(raw) {
  return PROMO_CODE_PATTERN.test(normalizePromoCode(raw));
}

/**
 * Cyrillic to Latin, so "Димитър" gives DIMITAR rather than nothing. Covers
 * Bulgarian, Russian, Ukrainian and Serbian letters; other scripts fall back
 * to a generic root.
 */
const CYRILLIC = {
  а: 'a', б: 'b', в: 'v', г: 'g', ґ: 'g', д: 'd', ђ: 'dj', е: 'e', ё: 'e', є: 'ye', ж: 'zh',
  з: 'z', и: 'i', і: 'i', ї: 'yi', й: 'y', ј: 'j', к: 'k', л: 'l', љ: 'lj', м: 'm', н: 'n',
  њ: 'nj', о: 'o', п: 'p', р: 'r', с: 's', т: 't', ћ: 'c', у: 'u', ф: 'f', х: 'h', ц: 'ts',
  ч: 'ch', џ: 'dz', ш: 'sh', щ: 'sht', ъ: 'a', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

const transliterate = (text) =>
  text.replace(/[\u0400-\u04ff]/g, (ch) => {
    const lower = ch.toLowerCase();
    return CYRILLIC[lower] ?? '';
  });

/**
 * The readable start of a generated code: the first word of a name, letters
 * only, up to 8 characters. "Rae Whitfield" → "RAE", "Zoë" → "ZOE",
 * "Димитър" → "DIMITAR". Empty when nothing usable is left (e.g. CJK).
 */
export function codeRoot(name) {
  const first = transliterate(String(name ?? ''))
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .split(/\s+/)[0];
  return (first ?? '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 8);
}

/**
 * Words brands use for their OWN storewide codes. A creator called "Summer"
 * must not be handed SUMMER10 automatically: if the store already runs that
 * code, every sale using it would be credited to the creator. Generated codes
 * built on these roots get extra letters; a brand renaming a code by hand is
 * trusted to know their own store.
 */
const STORE_CODE_WORDS = new Set(
  (
    'WELCOME HELLO HI SALE SAVE SAVING SAVINGS DEAL DEALS OFF FREE FREESHIP SHIP SHIPPING ' +
    'DISCOUNT PROMO CODE COUPON GIFT VIP NEW FIRST FIRSTORDER SUMMER SPRING WINTER FALL ' +
    'AUTUMN HOLIDAY HOLIDAYS XMAS CHRISTMAS EASTER CYBER BLACK BLACKFRIDAY FRIDAY WEEKEND ' +
    'FLASH LAUNCH THANKS THANKYOU FRIEND FRIENDS FAMILY STUDENT MEMBER MEMBERS NEWSLETTER ' +
    'EMAIL SMS BIRTHDAY LOYALTY REFER REFERRAL BOGO EXTRA BONUS TEST ADMIN STAFF TEAM ' +
    'EMPLOYEE CREATOR AFFILY'
  ).split(/\s+/),
);

/** True when a generated code on this root could clash with a store's own codes. */
export function isStoreCodeWord(root) {
  return STORE_CODE_WORDS.has(String(root ?? '').toUpperCase());
}

/**
 * The number a generated code ends in: the size of the discount, so "RAE10"
 * tells the shopper what they get. 10% → "10", $5 off → "5". Empty when there
 * is no discount, or when the percent is not whole (12.5% would read as "12").
 */
export function discountSuffix(codes) {
  if (!codes) return '';
  const whole = (n) => n > 0 && n % 100 === 0;
  if (codes.discountType === 'percent' && whole(codes.discountBps)) {
    return String(codes.discountBps / 100);
  }
  if (codes.discountType === 'fixed' && whole(codes.discountCents)) {
    return String(codes.discountCents / 100);
  }
  return '';
}

/**
 * A creator's code: name, then `extra` (letters the caller adds to get past a
 * collision), then the discount — "RAE10", or "RAEKM10" when RAE10 is taken.
 * Letters rather than digits for `extra`, so the number still reads as the
 * discount.
 *
 * `needsExtra` is true when the plain code must not be used even if free —
 * the name is a common store-code word, or there was no usable name at all.
 */
export function suggestPromoCode({ name, codes, extra = '' }) {
  const suffix = discountSuffix(codes);
  const named = codeRoot(name);
  let root = named || 'CREATOR';
  root = root.slice(0, PROMO_CODE_MAX_LENGTH - suffix.length - extra.length);
  const candidate = `${root}${extra}${suffix}`;
  // A one-letter name with no discount ("A") is too short to be a code.
  const code =
    candidate.length >= 3 ? candidate : `${candidate}${'X'.repeat(3 - candidate.length)}`;
  return { code, needsExtra: !extra && (!named || isStoreCodeWord(named)) };
}

/** "10% off", "$5.00 off", or null when the program's codes carry no discount. */
export function describeCodeDiscount(codes, { currency = 'usd' } = {}) {
  if (!codes) return null;
  if (codes.discountType === 'percent' && codes.discountBps > 0) {
    return `${codes.discountBps / 100}% off`;
  }
  if (codes.discountType === 'fixed' && codes.discountCents > 0) {
    return `${formatCents(codes.discountCents, { currency, dropZeroCents: true })} off`;
  }
  return null;
}
